import { IModel, IntegrationRuntime, IViolation, IAttribute, ModelConstants, ParameterValue, IValidationContext } from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';
import { Activity } from './activity';
import { UrlValidatorFactoryService } from 'app/extensibility/services/validator-factory/url-validator-factory.service';

import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import { HttpAuthenticationType } from './web.activity';
import { format } from 'app/shared/utils/string.util';
import { Owned } from '../base/named';
import { WebActivityLinkedService } from '../linkedservice/web-activity.linkedservice';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { GuardFeatures } from 'app/services/exposure-control/ec.typings';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { Copier } from 'app/model/base/copier';

export enum WebActivitySPNAuthenticationType {
  WEB_SERVICEPRINCIPAL_CLIENT_ID = 'username',
  WEB_SERVICEPRINCIPAL_SECRET = 'Password',
  WEB_SERVICEPRINCIPAL_CERTIFICATE = 'Pfx'
}

export enum WebhookActivitySettingsType {
    INLINE = 'inline',
    CONNECTION = 'connection'
}

@Metamodel.model({
    name: ModelConstants.webHookActivity,
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.WebHookActivityLabel),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.WebHookActivityLabel)
    ],
    referable: true
})
export class WebHookActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly URL: IAttribute;
    public static readonly RELATIVEURL: IAttribute;
    public static readonly RESOURCE: IAttribute;
    public static readonly BODY: IAttribute;
    public static readonly WEBHOOK_TIMEOUT: IAttribute;
    public static readonly AUTH_PROPERTY_GROUP_SIZE = 2;
    public static readonly DISABLECERTVALIDATION: IAttribute;
    public static readonly REPORTSTATUSONCALLBACK: IAttribute;
    public static readonly CREDENTIAL: IAttribute;
    public static readonly LINKED_SERVICE_PARAMETERS: IAttribute;
    public activitySettings: string = __IS_FABRIC_DPE__ ? WebhookActivitySettingsType.CONNECTION : WebhookActivitySettingsType.INLINE;

    @Metamodel.attribute({
        constraint: {
            required: true
        }
    })
    public method: 'POST' = 'POST'; // Currently we support only POST but might support PUT in the future

    @Metamodel.attribute({
        association: 'URL',
        templatable: true,
        constraint: {
            required: __IS_FABRIC_DPE__ ? false : true,
            validator: WebHookActivity.validateUrl
        }
    })
    public url: string;

    @Metamodel.attribute({
        association: 'RELATIVEURL',
        templatable: true
    })
    public relativeUrl: string;

    @Metamodel.attribute({
        association: 'RESOURCE',
        templatable: true
    })
    public resource?: string;

    @Metamodel.attribute({
        display: stringResources.Timeout,
        association: 'WEBHOOK_TIMEOUT',
        constraint: {
            validator: WebHookActivity.validateTimeout
        }
    })
    public webhookTimeout: string = '00:10:00';

    @Metamodel.attribute({
        constraint: {
            validator: WebHookActivity.validateHeader
        }
    })
    public headers: ParameterConfig[];

    @Metamodel.attribute({
        templatable: true,
        association: 'BODY',
        constraint: {
            validator: WebHookActivity.validateBody
        }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public body: any;

    @Metamodel.attribute({
        display: stringResources.IntegrationRuntime,
        reference: true
    })
    public connectVia: IntegrationRuntime;


   @Metamodel.attribute({
        display: stringResources.WebHookActivity_ReportStatusOnCallBack,
        association: 'REPORTSTATUSONCALLBACK',
        constraint: {
            required: false
        }
    })
    public reportStatusOnCallBack?: boolean;

    @Metamodel.attribute({
        constraint: {
            validator: WebHookActivity.validateAuthentication
        },
        type: HttpAuthenticationType.MODEL
    })
    public authentication: HttpAuthenticationType = Owned.create<HttpAuthenticationType>(HttpAuthenticationType.MODEL, this);
    public userTenant: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_DisableCertValidation,
        association: 'DISABLECERTVALIDATION',
        templatable: true
    })
    public disableCertValidation: boolean;

    @Metamodel.attribute({
        display: format(stringResources.ModelName_LinkedServiceTemplate, stringResources.WebActivityLabel),
        reference: true,
        type: WebActivityLinkedService.MODEL
    })
    public restLinkedService?: WebActivityLinkedService;

    @Metamodel.attribute({
        association: 'LINKED_SERVICE_PARAMETERS'
    })
    public linkedServiceParameters?: ParameterValue[] = [];

    public showDEPWarning: boolean = false;

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ExecutionActivity';
        this.authentication.type = 'None';
    }

    private static validateUrl(_webhookActivity?: WebHookActivity, url?: string): IViolation[] | undefined {
        if (_webhookActivity.activitySettings === WebhookActivitySettingsType.INLINE && new UrlValidatorFactoryService().createValidator()(url)) {
            return [
                {
                    message: () => stringResources.InvalidUrlLabel
                }
            ];
        }

        return undefined;
    }

    private static validateTimeout(_webhookActivity?: WebHookActivity, timeout?: string): IViolation[] | undefined {
        if (timeout && !(new RegExp('^(\\d+\\.)?((0?\\d)|(1\\d)|(2[0-3]))(:[0-5]?\\d){2}$').test(timeout))) {
            return [
                {
                    message: () => stringResources.WebHookActivity_Error_InvalidTimeout,
                    context: {
                        field: 'webhookTimeout'
                    }
                }
            ];
        }
        return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static validateBody(webhookActivity?: WebHookActivity, body?: any): IViolation[] | undefined {
        if (webhookActivity && webhookActivity.method === 'POST') {
            if (!body) {
                return [
                    {
                        message: () => stringResources.Activity_Error_BodyRequired
                    }
                ];
            }
            // Body should be valid JSON object. null and array are considered objects so check for those as well
            if (typeof body !== 'object' || body === null || Array.isArray(body)) {
                return [
                    {
                        message: () => stringResources.WebHookActivity_Invalid_Body
                    }
                ];
            }
        }
        return undefined;
    }
    private static validateHeader(_webhookActivity?: WebHookActivity, headers?: ParameterConfig[], violation?: IViolation): IViolation[] | undefined {
        if (!headers) {
            return undefined;
        }

        if (violation && violation.context) {
            // only validate in this violation's context
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const headerIndex = (violation.context as any).index;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const isKey = (violation.context as any).field === 'name';

            if (headerIndex < 0 || headerIndex >= headers.length) {
                // this could happen if a header row was deleted
                return undefined;
            }

            if (isKey) {
                return !!(headers[headerIndex].name) ? undefined : [violation];
            } else {
                return !!(headers[headerIndex].value) || !!ParameterConfig.VALUE.templateValue(headers[headerIndex]) ? undefined : [violation];
            }
        } else {
            // check all the headers
            const violations = new Array<IViolation>();

            for (let i = 0; i < headers.length; ++i) {
                if (!(headers[i].name)) {
                    violations.push({
                        message: () => stringResources.ErrorInvalidHeaderKey,
                        context: {
                            field: 'name',
                            index: i
                        }
                    });
                }

                if (!headers[i].value && !ParameterConfig.VALUE.templateValue(headers[i])) {
                    violations.push({
                        message: () => stringResources.ErrorInvalidHeaderValue,
                        context: {
                            field: 'value',
                            index: i
                        }
                    });
                }
            }

            return violations.length > 0 ? violations : undefined;
        }
    }

    private static async validateAuthentication(_webhookActivity?: WebHookActivity, authentication?: HttpAuthenticationType, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> {
        const violations = new Array<IViolation>();

        if (authentication) {
            if (violation) {
                // only validate against this violation
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const field: string = (violation.context as any).field;

                switch (field) {
                    case 'password':
                        return authentication.password ? undefined : [violation];
                    case 'username':
                        return authentication.username ? undefined : [violation];
                    case 'pfx':
                        return authentication.pfx ? undefined : [violation];
                    case 'resource':
                        return authentication.resource ? undefined : [violation];
                    case 'usertenant':
                        return authentication.userTenant ? undefined : [violation];
                    case 'credential':
                        return authentication.credential ? undefined : [violation];
                    default:
                        break;
                }
            } else {
                const ecSvc = (await getServiceInstance<ExposureControlService>(context.contextData, 'ExposureControlService'));
                // validate all the fields
                switch (authentication.type) {
                    case 'Basic':
                        if (!authentication.username && !HttpAuthenticationType.USERNAME.templateValue(authentication)) {
                            violations.push({
                                message: () => stringResources.WebActivity_Error_UsernameRequired,
                                context: {
                                    field: 'username'
                                }
                            });
                        }

                        if (!authentication.password) {
                            violations.push({
                                message: () => stringResources.WebActivity_Error_PasswordRequired,
                                context: {
                                    field: 'password'
                                }
                            });
                        }
                        break;
                    case 'ClientCertificate':
                        if (!authentication.pfx) {
                            violations.push({
                                message: () => stringResources.WebActivity_Error_PfxRequired,
                                context: {
                                    field: 'pfx'
                                }
                            });
                        }

                        if (!ecSvc.getCachedFeatureValue<boolean>(GuardFeatures.ADFOptionalPasswordToWebActivity) && !authentication.password) {
                            violations.push({
                                message: () => stringResources.WebActivity_Error_PasswordRequired,
                                context: {
                                    field: 'password'
                                }
                            });
                        }

                        break;
                    case 'MSI':
                        if (!authentication.resource && !HttpAuthenticationType.RESOURCE.templateValue(authentication)) {
                            violations.push({
                                message: () => stringResources.WebActivity_Error_ResourceRequired,
                                context: {
                                    field: 'resource'
                                }
                            });
                        }

                        if (authentication.authenticationMethod === stringResources.ModelName_Credential && !HttpAuthenticationType.CREDENTIAL.templateValue(authentication)) {
                            violations.push({
                                message: () => stringResources.WebActivity_Error_CredentialRequired,
                                context: {
                                    field: 'credential'
                                }
                            });
                        }

                        break;
                    case 'ServicePrincipal':
                        if (authentication.authenticationMethod === stringResources.Inline) {
                            if (authentication.spnAuthenticationFormat === WebActivitySPNAuthenticationType.WEB_SERVICEPRINCIPAL_CERTIFICATE &&
                                !authentication.pfx) {
                                violations.push({
                                    message: () => stringResources.WebActivity_Error_CertificateSecretRequired,
                                    context: {
                                        field: 'pfx'
                                    }
                                });
                            } else if (authentication.spnAuthenticationFormat === WebActivitySPNAuthenticationType.WEB_SERVICEPRINCIPAL_SECRET &&
                                !ecSvc.getCachedFeatureValue<boolean>(GuardFeatures.ADFOptionalPasswordToWebActivity) && !authentication.password) {
                                violations.push({
                                    message: () => stringResources.WebActivity_Error_CertificateSecretRequired,
                                    context: {
                                        field: 'password'
                                    }
                                });
                            }

                            if (!authentication.username && !HttpAuthenticationType.USERNAME.templateValue(authentication)) {
                                violations.push({
                                    message: () => stringResources.WebActivity_Error_ClientIdRequired,
                                    context: {
                                    field: 'username'
                                    }
                                });
                            }
                            if (!authentication.resource && !HttpAuthenticationType.RESOURCE.templateValue(authentication)) {
                                violations.push({
                                    message: () => stringResources.WebActivity_Error_ResourceRequired,
                                    context: {
                                    field: 'resource'
                                    }
                                });
                            }
                            if (!authentication.userTenant && !HttpAuthenticationType.USERTENANT.templateValue(authentication)) {
                                violations.push({
                                message: () => stringResources.WebActivity_Error_UserTenantRequired,
                                    context: {
                                    field: 'usertenant'
                                    }
                                });
                            }
                        } else if (authentication.authenticationMethod === stringResources.ModelName_Credential) {
                            if (!HttpAuthenticationType.CREDENTIAL.templateValue(authentication)) {
                                violations.push({
                                    message: () => stringResources.WebActivity_Error_CredentialRequired,
                                    context: {
                                        field: 'credential'
                                    }
                                });
                            }
                        }
                        break;
                    default:
                        break;
                }
            }
        }

        return violations.length > 0 ? violations : undefined;
    }

    public onActivityCloned(clonedActivity: WebHookActivity, context: { inlineDataset?: boolean }): void {
        if (__IS_FABRIC_DPE__) {
            if (this.restLinkedService?.parameterized) {
                clonedActivity.restLinkedService = Copier.clone(
                    context,
                    [this.restLinkedService],
                    undefined,
                    true
                )[0] as WebActivityLinkedService;
            }
        }
    }
}
